import React from 'react'
import styles from './TraitsMatch.module.scss'
import cx from 'classnames'
import { t } from 'ttag'
import { useTooltip } from 'hooks'
import { Box } from 'components/layout/Box/Box'
import { getSkillTraitTranslations } from 'components/modals/TestsReportModal/tabs/Competency/model'
import { TetherPortal } from 'components/shared/TetherPortal/TetherPortal'
import { Arrow } from 'components/shared/Arrow/Arrow'
import {
  TRAITS,
  TRAIT_DESCRIPTIONS,
} from 'components/common/ScoreCard/ScoreCard'
import { getTraitsTranslations } from 'components/modals/TestsReportModal/tabs/helpers'

const Row = ({
  trim,
  index,
  label,
  scores,
  isGreen,
  single,
  candidates,
  selectedCandidates,
  language,
}) => {
  const {
    state: {
      arrowRef,
      arrowStyle,
      floating,
      floatingStyle,
      reference,
      hasArrow,
      isToggled,
    },
    handlers: {
      handleBlur,
      handleFocus,
      handleMouseEnter,
      handleMouseLeave,
      handleTouchEnd,
      handleTouchStart,
    },
  } = useTooltip({ initialPlacement: 'left-end' })

  const traits = TRAITS[label]
  const description = TRAIT_DESCRIPTIONS[label]?.[language]
  const attributes = getTraitsTranslations(traits, [], language)
  const translatedLabel = getSkillTraitTranslations(label)

  return (
    <div className={styles.contentRow}>
      <div
        className={cx(styles.traitsCell, {
          [styles.trim]: trim,
        })}
        {...{
          ref: reference,
          onBlur: handleBlur,
          onFocus: handleFocus,
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
          onTouchStart: handleTouchStart,
          onTouchEnd: handleTouchEnd,
        }}
      >
        {translatedLabel}
      </div>
      {scores
        .filter(data =>
          selectedCandidates ? selectedCandidates[data.id] : true,
        )
        .map(data => {
          const candidateClass = single
            ? undefined
            : candidates.findIndex(cand => cand.id === data.id)
            ? styles.second
            : styles.first

          return (
            <div className={cx(styles.matchCell, { [styles.trim]: trim })}>
              {[...Array(6).keys()].map(key => (
                <div
                  key={key}
                  className={cx(styles.pill, candidateClass, {
                    [styles.isGreen]: isGreen,
                    [styles.filled]:
                      key <= Math.floor(data.scores[index].score * 6),
                  })}
                ></div>
              ))}
            </div>
          )
        })}

      <TetherPortal isOpen={isToggled && (traits?.length || description)}>
        <Box
          align="left"
          className={styles.message}
          justify="left"
          ref={floating}
          role="status"
          style={floatingStyle}
        >
          {hasArrow && (
            <Arrow
              ref={arrowRef}
              size="small"
              style={arrowStyle}
              theme="bright"
            />
          )}
          <Box
            align="start"
            className={styles.tooltipContent}
            direction="column"
            role="tooltip"
          >
            {traits?.length ? (
              <div
                className={styles.tooltipTitle}
              >{`${translatedLabel} ${t`facets`}`}</div>
            ) : null}
            {description ? (
              <div>{description}</div>
            ) : (
              attributes.map(({ label }) => (
                <div className={styles.tooltipItem} key={label}>
                  {label}
                </div>
              ))
            )}
          </Box>
        </Box>
      </TetherPortal>
    </div>
  )
}

export const TraitsMatch = ({
  data,
  single,
  language,
  candidates,
  selectedCandidates,
  trim = false,
  isGreen = false,
  compact,
}) => {
  return (
    <div className={cx(styles.traitsMatch, { [styles.compact]: compact })}>
      <div className={styles.header}>
        <div
          className={cx(styles.headerTraitsCell, {
            [styles.trim]: trim,
          })}
        >
          {trim ? t`Based on` : t`Based on the traits`}
        </div>
        {single ? (
          <div
            className={cx(styles.headerMatchCell, {
              [styles.trim]: trim,
            })}
          >
            {t`Match`}
          </div>
        ) : (
          data
            .filter(data => !selectedCandidates || selectedCandidates[data.id])
            .map(data => {
              const candidateClass = candidates.findIndex(
                cand => cand.id === data.id,
              )
                ? styles.second
                : styles.first

              return (
                <div
                  className={cx(styles.headerMatchCell, candidateClass, {
                    [styles.trim]: trim,
                  })}
                >
                  {data.name}
                </div>
              )
            })
        )}
      </div>
      <div className={styles.content}>
        {data[0].scores.map(({ label }, index) => {
          return (
            <Row
              key={label}
              single={single}
              trim={trim}
              label={label}
              scores={data}
              index={index}
              isGreen={isGreen}
              language={language}
              candidates={candidates}
              selectedCandidates={selectedCandidates}
            />
          )
        })}
      </div>
    </div>
  )
}
